import { Box } from "@mui/material";

import { formatPrice } from "../../utility/formatters";
import { DashboardOverviewCard } from "./DashboardOverviewCard";

const commonBoxStyles = {
  display: "flex",
  flex: 1,
  flexDirection: { xs: "column", sm: "row", md: "row", lg: "column" },
  gap: { xs: "0.375rem", sm: "0.75rem", md: "0.75rem", lg: "0.75rem" },
  "@media print": {
    flexDirection: "column",
  },
};

// Reusable component for stats column
const StatsColumn = ({ children }) => <Box sx={commonBoxStyles}>{children}</Box>;

export const DashboardStatsCards = ({
  revenueDetail,
  currentCrop,
  budgetTotal,
  farmPlantingData,
  isCropSpecific = false,
}) => {
  // Calculate values based on whether it's crop-specific or farm-wide
  const getTotalRevenue = () => {
    if (isCropSpecific && currentCrop) {
      return (
        revenueDetail?.crops
          ?.find((c) => c.name === currentCrop.name)
          ?.revenueData?.find((revenue) => /Total Revenue/i.test(revenue.label))?.value || 0
      );
    }
    return (
      revenueDetail?.crops?.reduce((total, crop) => {
        const projectedRevenue = crop.revenueData?.find((revenue) => /Total Revenue/i.test(revenue.label))?.value || 0;
        return total + projectedRevenue;
      }, 0) || 0
    );
  };

  const getTotalAcres = () => {
    if (isCropSpecific && currentCrop) {
      return currentCrop.rows.find((row) => row.practiceName === "Total")?.acres || 0;
    }
    return farmPlantingData?.totalAcres || 0;
  };

  const getTotalCost = () => {
    if (isCropSpecific && currentCrop) {
      return currentCrop.rows.find((row) => row.practiceName === "Total")?.totalCost || 0;
    }
    return budgetTotal || 0;
  };

  const getCostPerAcre = () => {
    if (isCropSpecific && currentCrop) {
      return currentCrop.rows.find((row) => row.practiceName === "Total")?.costPerAcre || 0;
    }
    return farmPlantingData?.costPerAcre || 0;
  };

  const totalRevenue = getTotalRevenue();
  const totalAcres = getTotalAcres();
  const totalCost = getTotalCost();
  const costPerAcre = getCostPerAcre();

  const revenuePerAcre = totalAcres > 0 ? totalRevenue / totalAcres : 0;
  const projectedIncome = totalRevenue - totalCost;
  const incomePerAcre = revenuePerAcre - costPerAcre;

  return (
    <Box
      sx={{
        backgroundColor: "var(--background-section)",
        borderRadius: "2.375rem",
        padding: {
          xs: "0.375rem",
          sm: "1rem",
          md: "1rem",
          lg: "1.375rem",
        },
        marginBottom: "1.5rem",
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "column", lg: "row" },
        gap: "0.75rem",
        "@media print": {
          margin: 0,
          borderRadius: 0,
          backgroundColor: "transparent",
          flexDirection: "row",
          padding: 0,
          "& > *": {
            margin: 0,
            padding: 0,
          },
        },
      }}
    >
      {/* Revenue Stats */}
      <StatsColumn>
        <DashboardOverviewCard
          title="Total Farm Revenue"
          value={totalRevenue ? formatPrice(totalRevenue, 0) : "$0.00"}
          variant="primary"
        />
        <DashboardOverviewCard title="Revenue Per Acre" value={formatPrice(revenuePerAcre, 0)} variant="secondary" />
      </StatsColumn>

      {/* Cost Stats */}
      <StatsColumn>
        <DashboardOverviewCard title="Cost of Production" value={totalCost ? formatPrice(totalCost, 0) : "$0.00"} />
        <DashboardOverviewCard
          title="Cost Per Acre"
          value={costPerAcre ? formatPrice(costPerAcre, 0) : "$0.00"}
          variant="secondary"
        />
      </StatsColumn>

      {/* Income Stats */}
      <StatsColumn>
        <DashboardOverviewCard title="Projected Income" value={formatPrice(projectedIncome, 0)} />
        <DashboardOverviewCard title="Income Per Acre" value={formatPrice(incomePerAcre, 0)} variant="secondary" />
      </StatsColumn>
    </Box>
  );
};
